import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  Settings, ArrowLeft, Save, Loader2, AlertTriangle, Info,
  Percent, DollarSign, Calendar, Building2
} from 'lucide-react';

export default function TributarioConfig() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    regime: 'lucro_presumido',
    presuncao_irpj: 0.32,
    presuncao_csll: 0.32,
    aliquota_irpj: 0.15,
    adicional_irpj: 0.10,
    limite_adicional_irpj: 20000,
    aliquota_csll: 0.09,
    aliquota_pis: 0.0065,
    aliquota_cofins: 0.03,
    aliquota_iss: 0.04,
    codigo_servico_iss: '08.01',
    municipio_iss: 'Taboão da Serra - SP',
    cnae: '8513900',
    aliquota_cbs: 0,
    aliquota_ibs: 0,
    usar_reforma_2026: false,
    limite_majoracao_anual: 5000000,
    percentual_majoracao: 0.10,
    aplicar_majoracao: false,
    vigencia_inicio: new Date().toISOString().split('T')[0],
    vigencia_fim: '',
    observacoes: 'Parâmetros devem ser validados com a contabilidade'
  });
  const [configId, setConfigId] = useState(null);
  
  const navigate = useNavigate();

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const response = await api.get('/tributario/config');
      if (response.data.configurado) {
        setConfig(response.data);
        setConfigId(response.data.id);
      }
    } catch (error) {
      console.error('Erro ao carregar configuração:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (configId) {
        await api.put(`/tributario/config/${configId}`, config);
      } else {
        await api.post('/tributario/config', config);
      }
      toast.success('Configuração salva com sucesso!');
      loadConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const handlePercentChange = (field, value) => {
    // Converter de percentual para decimal
    const decimal = parseFloat(value) / 100 || 0;
    setConfig(prev => ({ ...prev, [field]: decimal }));
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-[600px]" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="tributario-config">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/tributario')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Settings className="w-6 h-6" />
            Configurações Tributárias
          </h1>
          <p className="text-muted-foreground">Parâmetros do Lucro Presumido</p>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Salvando...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Salvar Configuração
            </>
          )}
        </Button>
      </div>

      {/* Aviso */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="flex items-start gap-3 pt-4">
          <Info className="w-5 h-5 text-blue-600 mt-0.5" />
          <div className="text-sm text-blue-700">
            <p className="font-medium">Importante:</p>
            <p>Estes parâmetros devem ser validados com a contabilidade da escola antes do uso em produção.</p>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="irpj_csll" className="space-y-4">
        <TabsList className="grid grid-cols-4 w-full max-w-2xl">
          <TabsTrigger value="irpj_csll">IRPJ/CSLL</TabsTrigger>
          <TabsTrigger value="pis_cofins">PIS/COFINS</TabsTrigger>
          <TabsTrigger value="iss">ISS</TabsTrigger>
          <TabsTrigger value="reforma">Reforma 2026</TabsTrigger>
        </TabsList>

        {/* IRPJ e CSLL */}
        <TabsContent value="irpj_csll">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Percent className="w-5 h-5" />
                IRPJ e CSLL - Lucro Presumido
              </CardTitle>
              <CardDescription>
                Parâmetros de presunção e alíquotas para serviços educacionais
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="font-medium">IRPJ</h4>
                  
                  <div className="space-y-2">
                    <Label htmlFor="presuncao_irpj">Percentual de Presunção IRPJ (%)</Label>
                    <Input
                      id="presuncao_irpj"
                      type="number"
                      step="0.01"
                      value={(config.presuncao_irpj * 100).toFixed(2)}
                      onChange={(e) => handlePercentChange('presuncao_irpj', e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">Padrão para serviços: 32%</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="aliquota_irpj">Alíquota IRPJ (%)</Label>
                    <Input
                      id="aliquota_irpj"
                      type="number"
                      step="0.01"
                      value={(config.aliquota_irpj * 100).toFixed(2)}
                      onChange={(e) => handlePercentChange('aliquota_irpj', e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">Padrão: 15%</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="adicional_irpj">Alíquota Adicional IRPJ (%)</Label>
                    <Input
                      id="adicional_irpj"
                      type="number"
                      step="0.01"
                      value={(config.adicional_irpj * 100).toFixed(2)}
                      onChange={(e) => handlePercentChange('adicional_irpj', e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">Padrão: 10% sobre excedente</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="limite_adicional">Limite Adicional IRPJ (R$/mês)</Label>
                    <Input
                      id="limite_adicional"
                      type="number"
                      step="1000"
                      value={config.limite_adicional_irpj}
                      onChange={(e) => handleChange('limite_adicional_irpj', parseFloat(e.target.value) || 0)}
                    />
                    <p className="text-xs text-muted-foreground">Padrão: R$ 20.000,00 por mês do trimestre</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-medium">CSLL</h4>
                  
                  <div className="space-y-2">
                    <Label htmlFor="presuncao_csll">Percentual de Presunção CSLL (%)</Label>
                    <Input
                      id="presuncao_csll"
                      type="number"
                      step="0.01"
                      value={(config.presuncao_csll * 100).toFixed(2)}
                      onChange={(e) => handlePercentChange('presuncao_csll', e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">Padrão para serviços: 32%</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="aliquota_csll">Alíquota CSLL (%)</Label>
                    <Input
                      id="aliquota_csll"
                      type="number"
                      step="0.01"
                      value={(config.aliquota_csll * 100).toFixed(2)}
                      onChange={(e) => handlePercentChange('aliquota_csll', e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">Padrão: 9%</p>
                  </div>
                </div>
              </div>

              {/* Majoração */}
              <div className="border-t pt-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="aplicar_majoracao">Aplicar Majoração (Receita &gt; R$ 5M)</Label>
                    <p className="text-xs text-muted-foreground">
                      Aumenta em 10% o percentual de presunção sobre receita excedente a R$ 5.000.000,00/ano
                    </p>
                  </div>
                  <Switch
                    id="aplicar_majoracao"
                    checked={config.aplicar_majoracao}
                    onCheckedChange={(checked) => handleChange('aplicar_majoracao', checked)}
                  />
                </div>
                
                {config.aplicar_majoracao && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Limite Anual para Majoração (R$)</Label>
                      <Input
                        type="number"
                        value={config.limite_majoracao_anual}
                        onChange={(e) => handleChange('limite_majoracao_anual', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Percentual de Majoração (%)</Label>
                      <Input
                        type="number"
                        value={(config.percentual_majoracao * 100).toFixed(0)}
                        onChange={(e) => handlePercentChange('percentual_majoracao', e.target.value)}
                      />
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PIS e COFINS */}
        <TabsContent value="pis_cofins">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                PIS e COFINS
              </CardTitle>
              <CardDescription>
                Regime Cumulativo (Lucro Presumido)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="aliquota_pis">Alíquota PIS (%)</Label>
                    <Input
                      id="aliquota_pis"
                      type="number"
                      step="0.01"
                      value={(config.aliquota_pis * 100).toFixed(2)}
                      onChange={(e) => handlePercentChange('aliquota_pis', e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">Padrão regime cumulativo: 0,65%</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="aliquota_cofins">Alíquota COFINS (%)</Label>
                    <Input
                      id="aliquota_cofins"
                      type="number"
                      step="0.01"
                      value={(config.aliquota_cofins * 100).toFixed(2)}
                      onChange={(e) => handlePercentChange('aliquota_cofins', e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">Padrão regime cumulativo: 3%</p>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm">
                  <strong>Fundamento Legal:</strong> Lei 10.637/2002 (PIS) e Lei 10.833/2003 (COFINS) - Regime Cumulativo
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ISS */}
        <TabsContent value="iss">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                ISS - Imposto Sobre Serviços
              </CardTitle>
              <CardDescription>
                Configuração municipal para serviços educacionais
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="aliquota_iss">Alíquota ISS (%)</Label>
                    <Input
                      id="aliquota_iss"
                      type="number"
                      step="0.01"
                      value={(config.aliquota_iss * 100).toFixed(2)}
                      onChange={(e) => handlePercentChange('aliquota_iss', e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">Taboão da Serra: 4% para educação</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="municipio_iss">Município</Label>
                    <Input
                      id="municipio_iss"
                      value={config.municipio_iss}
                      onChange={(e) => handleChange('municipio_iss', e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="codigo_servico_iss">Código de Serviço (LC 116)</Label>
                    <Input
                      id="codigo_servico_iss"
                      value={config.codigo_servico_iss}
                      onChange={(e) => handleChange('codigo_servico_iss', e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">08.01 - Ensino regular</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="cnae">CNAE</Label>
                    <Input
                      id="cnae"
                      value={config.cnae}
                      onChange={(e) => handleChange('cnae', e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">8513900 - Ensino fundamental</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reforma Tributária 2026 */}
        <TabsContent value="reforma">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                Reforma Tributária 2026 (CBS/IBS)
              </CardTitle>
              <CardDescription>
                Preparação para a transição do sistema tributário
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 bg-amber-50 rounded-lg">
                <p className="text-sm text-amber-700">
                  <strong>Atenção:</strong> Os parâmetros abaixo são para simulação e preparação. 
                  As alíquotas finais da CBS e IBS ainda estão em definição pelo Congresso.
                </p>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="usar_reforma_2026">Habilitar Simulação CBS/IBS</Label>
                  <p className="text-xs text-muted-foreground">
                    Permite simular cenários com as novas alíquotas
                  </p>
                </div>
                <Switch
                  id="usar_reforma_2026"
                  checked={config.usar_reforma_2026}
                  onCheckedChange={(checked) => handleChange('usar_reforma_2026', checked)}
                />
              </div>

              {config.usar_reforma_2026 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="aliquota_cbs">Alíquota CBS (%)</Label>
                    <Input
                      id="aliquota_cbs"
                      type="number"
                      step="0.01"
                      value={(config.aliquota_cbs * 100).toFixed(2)}
                      onChange={(e) => handlePercentChange('aliquota_cbs', e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">Substituirá PIS/COFINS (Federal)</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="aliquota_ibs">Alíquota IBS (%)</Label>
                    <Input
                      id="aliquota_ibs"
                      type="number"
                      step="0.01"
                      value={(config.aliquota_ibs * 100).toFixed(2)}
                      onChange={(e) => handlePercentChange('aliquota_ibs', e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">Substituirá ISS/ICMS (Estadual/Municipal)</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Vigência e Observações */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Vigência e Observações
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="vigencia_inicio">Início da Vigência</Label>
              <Input
                id="vigencia_inicio"
                type="date"
                value={config.vigencia_inicio}
                onChange={(e) => handleChange('vigencia_inicio', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="vigencia_fim">Fim da Vigência (opcional)</Label>
              <Input
                id="vigencia_fim"
                type="date"
                value={config.vigencia_fim || ''}
                onChange={(e) => handleChange('vigencia_fim', e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="observacoes">Observações</Label>
            <Textarea
              id="observacoes"
              value={config.observacoes}
              onChange={(e) => handleChange('observacoes', e.target.value)}
              rows={3}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
